import "../i18nFallback";
import { Sentry } from "../sentryInstrumentReact";
import React, { useState, useEffect } from 'react';

import AddressFormInput from './AddressFormInput';
import AddressFormSelect from './AddressFormSelect';
import { useCart } from './CartContext';
import countryRegionDataJSON from "../../data/countryRegion.json";

function changeInputValue(input, value) {
  input.value = value;
  input.dispatchEvent(new Event("change"));
}

function getPlaceAutocompleteInput(placeAutocomplete) {
  return (
    placeAutocomplete.Dg ||
    placeAutocomplete.Eg ||
    placeAutocomplete.Fg ||
    Object.values(placeAutocomplete).find((v) => v instanceof HTMLInputElement)
  );
}

function isAddressComplete(typeData) {
  return Boolean(
    (
      (typeData.firstName && typeData.lastName) ||
      typeData.organization
    ) &&
    typeData.street1 &&
    typeData.city &&
    typeData.region &&
    typeData.postalCode &&
    typeData.postalCode.length > 4 &&
    typeData.country
  );
}

function checkoutErrorPathForField(type, name) {
  if (name === "country") {
    return type === "billing" ? ["country"] : ["shipping_country"];
  }
  if (name === "region") {
    return type === "billing" ? ["region"] : ["shipping_region"];
  }

  const fieldNames = {
    firstName: "first_name",
    lastName: "last_name",
    postalCode: "post",
  };

  return [type, fieldNames[name] || name];
}

function addressAutocomplete(section, token) {
  return `${section} ${token}`;
}

const street1NativeInputCallbacks = {};

function enableNativeStreet1Input(addrType) {
  street1NativeInputCallbacks[addrType]?.();
}

function AddressForm({ type }) {
  const { checkoutFormData, setCheckoutFormData, checkoutErrors, shippingRequired, clearCheckoutFieldError } = useCart();
  const [canadaShippingNotice, setCanadaShippingNotice] = useState('');
  const [countryRegionData, setCountryRegionData] = useState([]);
  const [street1UseNativeInput, setStreet1UseNativeInput] = useState({});

  useEffect(() => {
    street1NativeInputCallbacks[type] = () => {
      setStreet1UseNativeInput((prev) => ({ ...prev, [type]: true }));
    };
    return () => {
      delete street1NativeInputCallbacks[type];
    };
  }, [type]);

  // fetch country data
  useEffect(() => {
    async function fetchCountryAndRegions(retries) {
	  let response;
      try {
        response = await fetch("/geo/_api/countries/", {
          method: 'GET',
          headers: {"Content-Type": "application/json"},
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        if ((type === "shipping") || (shippingRequired)) {
          const filteredData = data.filter((country) =>
            country.code === "USA" || country.code === "US" || country.code === "CA"
          );
          setCountryRegionData(filteredData);
        } else {
          setCountryRegionData(data);
        }

      } catch (error) {

        if (retries > 0) {
          await new Promise(r => setTimeout(r, 1000));	// NOTE: sleep 1 second
          return await fetchCountryAndRegions(retries - 1);
        } else {
          if ((type === "shipping") || (shippingRequired)) {
            const filteredData = countryRegionDataJSON.filter((country) =>
              country.code === "USA" || country.code === "US" || country.code === "CA"
            );
            setCountryRegionData(filteredData);
          } else {
            setCountryRegionData(countryRegionDataJSON);
          }
        }
		if (Sentry && Sentry.getClient()) {
          Sentry.captureException(error, {retries: retries, stage: "fetchCountryAndRegions", response: response});
		}
      }
    }
    fetchCountryAndRegions(4);
  }, [type, shippingRequired]);

  // set up autocomplete
  useEffect(() => {
    for (const addrType of ["billing", "shipping"]) {
      let street1Input = document.getElementById(`${addrType}_street1`);
	  if (street1Input === null) {
		continue;
	  }
	  const autocompleteElementOptions = {types: ["address"], noInputIcon: true, noClearButton: true};

	  if (shippingRequired) {
		autocompleteElementOptions.includedRegionCodes = ["us", "ca"];
	  }
	  const placeAutocomplete = new google.maps.places.PlaceAutocompleteElement(autocompleteElementOptions);
	  placeAutocomplete.id = `${street1Input.id}-autocomplete`;
	  placeAutocomplete.placeholder = street1Input.placeholder;
	  street1Input.after(placeAutocomplete);
	  street1Input.classList.add("autocomplete");
	  street1Input.style.display = "none";

	  // NOTE: redirect the label click to the visible autocomplete input
	  const street1Label = document.querySelector(`label[for="${street1Input.id}"]`);
	  if (street1Label !== null) {
		street1Label.addEventListener("click", (event) => {
		  event.preventDefault();
		  getPlaceAutocompleteInput(placeAutocomplete)?.focus();
		});
	  }
	  const street1AutocompleteInput = getPlaceAutocompleteInput(placeAutocomplete);
	  if (street1AutocompleteInput) {
		street1AutocompleteInput.setAttribute(
		  "autocomplete",
		  addressAutocomplete(addrType, "address-line1")
		);
		street1AutocompleteInput.addEventListener("focus", () =>
		  street1Input.dispatchEvent(new FocusEvent("focusin", { bubbles: true }))
		);
		street1AutocompleteInput.addEventListener("blur", () =>
		  street1Input.dispatchEvent(new FocusEvent("focusout", { bubbles: true }))
		);
	  }

	  placeAutocomplete.addEventListener("gmp-error", () => {
		console.error("Google Autocomplete failed, re-enabling street1 input field");
		enableNativeStreet1Input(addrType);
		street1Input.disabled = false;
		street1Input.classList.remove("autocomplete", "pac-target-input", "gm-err-autocomplete");
		street1Input.style.backgroundImage = "";
		street1Input.focus();
		street1Input.hidden = false;
		street1Input.value = placeAutocomplete.Ai.shadowRoot.querySelector("input").value || "";
		placeAutocomplete.remove();
	  });
	  window.gm_authFailure = function() {
		console.error("Google Autocomplete failed, re-enabling street1 input field");
		street1Input.disabled = false;
		street1Input.classList.remove("pac-target-input", "gm-err-autocomplete");
		street1Input.style.backgroundImage = "";
		street1Input.focus();
	  }

	  const onGMPSelect = async ({ placePrediction }) => {
		const place = placePrediction.toPlace();
		if (place === null) {
		  return;
		}
		await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] });
		const components = Object.fromEntries(place.addressComponents.map(component => [component.types[0], component]));
		const placeAutocompleteInput = getPlaceAutocompleteInput(placeAutocomplete);
		let street1, street2 = "";
		if (components.subpremise && place.displayName.endsWith(components.subpremise.longText)) {
		  street1 = place.displayName.replace(components.subpremise.longText, "").trim();
		  changeInputValue(placeAutocompleteInput, place.displayName.replace(components.subpremise.longText, "").trim())
		  street2 = components.subpremise.longText;
		} else {
		  street1 = place.displayName;
		  changeInputValue(placeAutocompleteInput, place.displayName);
		}

		// set city
		let city = "";
		if (components.locality) {
		  city = components.locality.shortText;
		} else if (components.administrative_area_level_2) {
		  city = components.administrative_area_level_2.longText;
		}

		// set postal code
		let postalCode = components.postal_code ? components.postal_code.longText : ""
		if (postalCode !== "" && components.postal_code_suffix) {
		  postalCode = `${postalCode}-${components.postal_code_suffix.longText || components.postal_code_suffix.shortText}`;
		}

        for (const fieldName of [
          "street1",
          "street2",
          "city",
          "postalCode",
          "region",
          "country",
        ]) {
          clearCheckoutFieldError(checkoutErrorPathForField(addrType, fieldName));
        }

		const country = (components.country.shortText === "US") ? "USA" : components.country.shortText;
		// set region
		let region = "";
		const administrativeAreaFields = Object.keys(components).filter(k => k.startsWith("administrative_area")).sort();
		if (administrativeAreaFields.length) {
		  administrativeArea = components[administrativeAreaFields[0]];
		  region = administrativeArea.shortText.normalize('NFKD').replace(/[\u0300-\u036f]/g, '') || administrativeArea.longText.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
		} else {
		  console.error("No region data from autocomplete");
		}

		setCheckoutFormData((prevFormData) => {
          const updatedTypeData = {
            ...prevFormData[addrType],
            street1: street1,
            street2: street2,
            city: city,
            region: region,
            postalCode: postalCode,
            country: country,
          };
          return {
            ...prevFormData,
            [addrType]: {
              ...updatedTypeData,
              addressComplete: isAddressComplete(updatedTypeData),
            }
          };
		});
	  };
	  placeAutocomplete.addEventListener("gmp-select", onGMPSelect);

	  // Sync manual typing / browser autofill in the visible Google autocomplete
	  // input back into React state so addressComplete reflects manual entry too.
	  placeAutocomplete.addEventListener("input", () => {
		const currentValue = getPlaceAutocompleteInput(placeAutocomplete)?.value ?? "";
		setCheckoutFormData((prevFormData) => {
		  if (prevFormData[addrType].street1 === currentValue) {
			return prevFormData;
		  }
		  const updatedTypeData = {
			...prevFormData[addrType],
			street1: currentValue,
		  };
		  return {
			...prevFormData,
			[addrType]: {
			  ...updatedTypeData,
			  addressComplete: isAddressComplete(updatedTypeData),
			}
		  };
		});
	  });

	  changeInputValue(getPlaceAutocompleteInput(placeAutocomplete), street1Input.value || "");
    }
  }, [clearCheckoutFieldError]);

  // Keep the visible Google autocomplete input in sync when street1 is pre-filled
  // after init (account data, browser autofill on the hidden React input).
  useEffect(() => {
    const autocompleteEl = document.getElementById(`${type}_street1-autocomplete`);
    if (!autocompleteEl) {
      return;
    }
    const placeAutocompleteInput = getPlaceAutocompleteInput(autocompleteEl);
    if (
      placeAutocompleteInput &&
      checkoutFormData[type].street1 &&
      placeAutocompleteInput.value !== checkoutFormData[type].street1
    ) {
      placeAutocompleteInput.value = checkoutFormData[type].street1;
    }
  }, [checkoutFormData[type].street1, type]);


  useEffect(() => {
    async function fetchCanadaShippingFlatblock() {
      try {
        response = await fetch("/flatblocks/_api/flatblocks/canada-shipping-notice", {
          method: 'GET',
          headers: {"Content-Type": "application/json"},
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setCanadaShippingNotice(data.content);

      } catch (error) {
        console.log(error);
      }
    }
    fetchCanadaShippingFlatblock();
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;
    clearCheckoutFieldError(checkoutErrorPathForField(type, name));
    setCheckoutFormData((prevFormData) => {
      const updatedTypeData = {
        ...prevFormData[type],
        [name]: value,
      };
      return {
        ...prevFormData,
        [type]: {
          ...updatedTypeData,
          addressComplete: isAddressComplete(updatedTypeData),
        }
      }
    });

  };


  return (
    <>
      <div className="checkout-body shipping-information">
        <div className="form-grid-gap">
          <div className="form-group-row">
            <AddressFormInput
              type="text"
              name="firstName"
              label={window.gettext("First Name")}
              value={checkoutFormData[type].firstName}
              onChange={handleChange}
              maxLength="20"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.first_name ?? ""}
              autocomplete={addressAutocomplete(type, "given-name")}
            />
            <AddressFormInput
              type="text"
              name="lastName"
              label={window.gettext("Last Name")}
              value={checkoutFormData[type].lastName}
              onChange={handleChange}
              maxLength="20"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.last_name ?? ""}
              autocomplete={addressAutocomplete(type, "family-name")}
            />
          </div>
          {type === "billing" &&
            <div className="form-group-row">
              <AddressFormInput
                type="email"
                name="email"
                label={window.gettext("Email")}
                value={checkoutFormData[type].email}
                onChange={handleChange}
                maxLength="300"
                required
                idPrefix={type}
                errors={checkoutErrors?.[type]?.email ?? ""}
                autocomplete={addressAutocomplete(type, "email")}
              />
              <AddressFormInput
                type="tel"
                name="phone"
                label={window.gettext("Phone")}
                value={checkoutFormData[type].phone}
                onChange={handleChange}
                maxLength="25"
                required
                idPrefix={type}
                errors={checkoutErrors?.[type]?.phone ?? ""}
                autocomplete={addressAutocomplete(type, "tel")}
              />
            </div>
          }
          <div className="form-group-row">
            <AddressFormInput
              type="text"
              name="organization"
              label={window.gettext("Organization Name (Optional)")}
              placeholder={window.gettext(
                "Leave blank unless billing to a church or organization"
              )}
              value={checkoutFormData[type].organization}
              onChange={handleChange}
              maxLength="70"
              idPrefix={type}
              errors={checkoutErrors?.[type]?.organization ?? ""}
              autocomplete={addressAutocomplete(type, "organization")}
            />
          </div>
          <AddressFormInput
            type="text"
            name="street1"
            label={window.gettext("Address")}
            value={checkoutFormData[type].street1}
            onChange={handleChange}
            maxLength="70"
            required
            idPrefix={type}
            errors={checkoutErrors?.[type]?.street1 ?? ""}
            placeholder=""
            autocomplete={
              street1UseNativeInput[type]
                ? addressAutocomplete(type, "address-line1")
                : "off"
            }
          />
          <AddressFormInput
            type="text"
            name="street2"
            label={window.gettext("Address 2")}
            value={checkoutFormData[type].street2}
            onChange={handleChange}
            maxLength="70"
            idPrefix={type}
            errors={checkoutErrors?.[type]?.street2 ?? ""}
            autocomplete={addressAutocomplete(type, "address-line2")}
          />
          <div className="form-group-row">
            <AddressFormSelect
              containerDivId={`${type}_div_id_country`}
              labelText={window.gettext("Country")}
              optionsArray={[{value: '', label: window.gettext("-- Select --")}].concat(
                countryRegionData.map((country) => ({
                  value: country.code,
                  label: country.description,
                }))
              )}
              selectName="country"
              selectId={`${type}_div_id_country`}
              selectValue={checkoutFormData[type].country}
              onChange={handleChange}
              errors={type === 'billing' ? checkoutErrors.country : checkoutErrors.shipping_country}
              autocomplete={addressAutocomplete(type, "country")}
            />
          </div>
          <div className="form-group-row">
            <AddressFormInput
              type="text"
              name="city"
              label={window.gettext("City")}
              value={checkoutFormData[type].city}
              onChange={handleChange}
              maxLength="70"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.city ?? ""}
              autocomplete={addressAutocomplete(type, "address-level2")}
            />
            <AddressFormSelect
              containerDivId={`${type}_div_id_region`}
              labelText={window.gettext("State")}
              optionsArray={[{value: '', label: window.gettext("-- Select State --")}].concat(
                (countryRegionData.find((element) => element.code === checkoutFormData[type].country)?.regions || [])
                .map(region => ({
                  value: region.id,
                  label: region.description,
                }))
              )}
              selectName="region"
              selectId={`${type}_div_id_region`}
              selectValue={checkoutFormData[type].region}
              onChange={handleChange}
              errors={type === 'billing' ? checkoutErrors.region : checkoutErrors.shipping_region}
              autocomplete={addressAutocomplete(type, "address-level1")}
            />
            <AddressFormInput
              type="text"
              name="postalCode"
              label={window.gettext("Zip/Postal Code")}
              value={checkoutFormData[type].postalCode}
              onChange={handleChange}
              maxLength="15"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.post ?? ""}
              autocomplete={addressAutocomplete(type, "postal-code")}
            />
				  <AddressFormInput
              type="hidden"
              name="autocompleteDigest"
              value={checkoutFormData[type].autocompleteDigest}
              idPrefix={type}
            />
          </div>
          {checkoutFormData[type].country == "CA" &&
            <div className="shipping-msg">
              <p>{canadaShippingNotice}</p>
            </div>
          }
        </div>
      </div>
    </>
  );

}

export default AddressForm;
